// src/pages/MergeByDateAll.jsx
import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { API as DEFAULT_API } from "../apiBase";

function getApiBaseFallback() {
  try {
    const u = new URL(window.location.href);
    const p = u.searchParams.get("apiBase");
    if (p && p.trim()) return p.trim().replace(/\/+$/, "");
  } catch {}
  return (DEFAULT_API || "").replace(/\/+$/, "");
}

export default function MergeByDateAll() {
  const { token } = useAuth();
  const BASE = useMemo(getApiBaseFallback, []);
  const [date, setDate] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth()+1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  });

  const [branchesRoot, setBranchesRoot] = useState(""); // optional
  const [backup, setBackup] = useState(false);
  const [includeServerTable, setIncludeServerTable] = useState(false);
  const [force, setForce] = useState(true); // usually true if you run before 23:15
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }, [token]);

  async function runMerge() {
    setRunning(true);
    setError("");
    setResult(null);

    // preferred: via proxy/echo (bypasses CF 405)
    const q = new URLSearchParams();
    q.set("action", "merge_by_date_all_branches");
    q.set("date", date);
    if (force) q.set("force", "true");
    if (backup) q.set("backup", "true");
    if (includeServerTable) q.set("include_server_table", "true");
    if (branchesRoot.trim()) q.set("branches_root", branchesRoot.trim());

    try {
      const url1 = `${BASE}/proxy/echo?${q.toString()}`;
      const res1 = await fetch(url1, { method: "POST", headers: authHeaders, body: "{}" });
      const txt1 = await res1.text();
      let data1;
      try { data1 = JSON.parse(txt1); } catch { data1 = { message: txt1 }; }
      if (!res1.ok) {
        throw new Error(`${res1.status} ${res1.statusText} — ${data1?.detail || data1?.message || "Request failed"}`);
      }
      setResult(data1);
    } catch (e1) {
      // fallback: direct route (works in local/dev; CF may 405 on prod)
      try {
        const url2 = `${BASE}/merge-by-date/all-branches?date=${encodeURIComponent(date)}&force=${force ? "true" : "false"}${backup ? "&backup=true" : ""}${includeServerTable ? "&include_server_table=true" : ""}${branchesRoot.trim() ? `&branches_root=${encodeURIComponent(branchesRoot.trim())}` : ""}`;
        const res2 = await fetch(url2, { method: "GET", headers: authHeaders });
        const txt2 = await res2.text();
        let data2;
        try { data2 = JSON.parse(txt2); } catch { data2 = { message: txt2 }; }
        if (!res2.ok) {
          throw new Error(`${res2.status} ${res2.statusText} — ${data2?.detail || data2?.message || "Request failed"}`);
        }
        setResult(data2);
      } catch (e2) {
        setError((e1?.message || String(e1)) + " | fallback: " + (e2?.message || String(e2)));
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: 840, margin: "0 auto", padding: 16 }}>
      <h1 style={{ marginBottom: 12 }}>Merge by Date — All Branches</h1>
      <p style={{ opacity: 0.85, marginBottom: 16 }}>
        Scan all branch <code>table.db</code>/<code>table_*.db</code>, copy matches for the date into <code>contacts.db</code> on the server.
        Duplicates (by last 8 digits of phone) are skipped.
      </p>

      <div className="card" style={{ padding: 16, display: "grid", gap: 12 }}>
        <label style={{ display: "grid", gap: 6 }}>
          <span>Date (YYYY-MM-DD)</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="dialog-input"
            style={{ maxWidth: 220 }}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Branches root (optional)</span>
          <input
            type="text"
            value={branchesRoot}
            onChange={(e) => setBranchesRoot(e.target.value)}
            placeholder="e.g. C:\\Users\\you\\Desktop\\web-v2\\contact-web"
            className="dialog-input"
          />
          <small style={{ opacity: 0.75 }}>
            If empty, it scans the backend folder that contains <code>contacts.db</code>.
          </small>
        </label>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>Force (allow before 23:15)</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={backup} onChange={(e) => setBackup(e.target.checked)} />
            <span>Save backups</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={includeServerTable}
              onChange={(e) => setIncludeServerTable(e.target.checked)}
            />
            <span>Include server local table.db</span>
          </label>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn primary" onClick={runMerge} disabled={running}>
            {running ? "Merging…" : "Run Merge"}
          </button>
          <span style={{ alignSelf: "center", opacity: 0.7 }}>
            API: <code>{BASE}</code>
          </span>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 12, marginTop: 16, border: "1px solid rgba(255,0,0,.3)" }}>
          <strong>❌ Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="card" style={{ padding: 12, marginTop: 16 }}>
          <div style={{ display: "grid", gap: 8 }}>
            <div><strong>Inserted:</strong> {result.inserted}</div>
            <div><strong>Skipped:</strong> {result.skipped}</div>
            <div><strong>Sources scanned:</strong> {result.sources_scanned}</div>
            <div><strong>Scan root:</strong> {result.root || "(default)"}</div>

            {!!(result.sources_used?.length) && (
              <details open style={{ marginTop: 8 }}>
                <summary><strong>Sources used</strong></summary>
                <ul style={{ marginTop: 6 }}>
                  {result.sources_used.map((s, i) => <li key={i} style={{ wordBreak: "break-all" }}>{s}</li>)}
                </ul>
              </details>
            )}

            {!!(result.sources_ignored?.length) && (
              <details style={{ marginTop: 8 }}>
                <summary><strong>Sources ignored</strong></summary>
                <ul style={{ marginTop: 6 }}>
                  {result.sources_ignored.map((s, i) => <li key={i} style={{ wordBreak: "break-all" }}>{s}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
